import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { client } from '../api/client';

interface ListingDuration {
  id: number;
  days: number;
  tokens: number;
  label: string;
  for_auction_only?: boolean;
}

interface TokenPackage {
  id: string;
  tokens: number;
  price: number;
  currency: string;
  popular: boolean;
}

export function PricingPage() {
  const { language } = useI18n();
  const [listingDurations, setListingDurations] = useState<ListingDuration[]>([]);
  const [auctionDurations, setAuctionDurations] = useState<ListingDuration[]>([]);
  const [tokenPackages, setTokenPackages] = useState<TokenPackage[]>([]);
  const [loading, setLoading] = useState(true);

  // Fallback data in case API is unavailable
  const FALLBACK_DURATIONS: ListingDuration[] = [
    { id: 1, days: 7, tokens: 1, label: '7 dana' },
    { id: 2, days: 15, tokens: 3, label: '15 dana' },
    { id: 3, days: 30, tokens: 5, label: '30 dana' },
  ];
  const FALLBACK_AUCTION_DURATIONS: ListingDuration[] = [
    { id: 1, days: 7, tokens: 1, label: '7 dana' },
    { id: 2, days: 15, tokens: 3, label: '15 dana' },
    { id: 3, days: 30, tokens: 5, label: '30 dana' },
    { id: 4, days: 60, tokens: 7, label: '60 dana' },
    { id: 5, days: 90, tokens: 10, label: '90 dana' },
  ];
  const FALLBACK_PACKAGES: TokenPackage[] = [
    { id: '1', tokens: 5, price: 480, currency: 'RSD', popular: false },
    { id: '2', tokens: 10, price: 900, currency: 'RSD', popular: true },
    { id: '3', tokens: 20, price: 1700, currency: 'RSD', popular: false },
    { id: '4', tokens: 30, price: 2500, currency: 'RSD', popular: false },
  ];

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const [durRes, auctionRes, pkgRes] = await Promise.all([
          client.api.fetch('/api/pricing/listing-durations?ad_kind=Ponuda'),
          client.api.fetch('/api/pricing/listing-durations'),
          client.api.fetch('/api/pricing/token-packages'),
        ]);

        let durLoaded = false, auctionLoaded = false, pkgLoaded = false;

        if (durRes.ok) {
          const data = await durRes.json();
          if (data.data && data.data.length > 0) {
            setListingDurations(data.data);
            durLoaded = true;
          }
        }
        if (auctionRes.ok) {
          const data = await auctionRes.json();
          if (data.data && data.data.length > 0) {
            setAuctionDurations(data.data);
            auctionLoaded = true;
          }
        }
        if (pkgRes.ok) {
          const data = await pkgRes.json();
          if (data.data && data.data.length > 0) {
            setTokenPackages(data.data.map((pkg: any) => ({
              id: String(pkg.id),
              tokens: Number(pkg.tokens),
              price: Number(pkg.price || pkg.price_rsd),
              currency: pkg.currency || 'RSD',
              popular: Boolean(pkg.popular || pkg.is_popular),
            })));
            pkgLoaded = true;
          }
        }

        // Use fallbacks for any data that didn't load
        if (!durLoaded) setListingDurations(FALLBACK_DURATIONS);
        if (!auctionLoaded) setAuctionDurations(FALLBACK_AUCTION_DURATIONS);
        if (!pkgLoaded) setTokenPackages(FALLBACK_PACKAGES);
      } catch (err) {
        console.error('Failed to load pricing:', err);
        // Use all fallbacks on complete failure
        setListingDurations(FALLBACK_DURATIONS);
        setAuctionDurations(FALLBACK_AUCTION_DURATIONS);
        setTokenPackages(FALLBACK_PACKAGES);
      } finally {
        setLoading(false);
      }
    };
    loadPricing();
  }, []);

  const listingPrices = listingDurations.map((d, i) => ({
    duration: `${d.days} ${language === 'sr' ? 'dana' : 'days'}`,
    tokens: d.tokens,
    perDay: (d.tokens / d.days).toFixed(2),
    popular: i === Math.floor(listingDurations.length / 2),
  }));

  const auctionPrices = auctionDurations.map(d => ({
    duration: `${d.days} ${language === 'sr' ? 'dana' : 'days'}`,
    tokens: d.tokens,
  }));

  const bestPerToken = tokenPackages.length > 0
    ? Math.min(...tokenPackages.map(p => p.price / p.tokens))
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Cenovnik oglašavanja' : 'Advertising Pricing'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {language === 'sr' ? 'Transparentne cene za sve tipove oglasa' : 'Transparent prices for all listing types'}
            </p>
          </div>

          {/* Token Explanation */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <span className="text-3xl">💰</span>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {language === 'sr' ? 'Kako funkcioniše token sistem?' : 'How does the token system work?'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr' 
                    ? 'Na portalu Svoj Na Svom koristi se token sistem za oglašavanje. Tokeni su virtualna valuta koju koristite za objavu oglasa. Tokeni nikada ne ističu dok ih ne iskoristite.'
                    : 'Svoj Na Svom uses a token system for advertising. Tokens are virtual currency used to publish listings. Tokens never expire until used.'}
                </p>
              </div>
            </div>
          </div>

          {/* Free Tokens */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
              🎁 {language === 'sr' ? 'Besplatni tokeni pri registraciji' : 'Free tokens on registration'}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                <span className="text-2xl mb-2 block">👤</span>
                <h3 className="font-medium text-slate-900 dark:text-white">
                  {language === 'sr' ? 'Fizička lica' : 'Individuals'}
                </h3>
                <p className="text-2xl font-bold text-amber-500 my-2">5 {language === 'sr' ? 'tokena' : 'tokens'}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'besplatno nakon verifikacije' : 'free after verification'}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 text-center">
                <span className="text-2xl mb-2 block">🏢</span>
                <h3 className="font-medium text-slate-900 dark:text-white">
                  {language === 'sr' ? 'Pravna lica' : 'Companies'}
                </h3>
                <p className="text-2xl font-bold text-amber-500 my-2">20 {language === 'sr' ? 'tokena' : 'tokens'}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'besplatno nakon verifikacije firme' : 'free after company verification'}
                </p>
              </div>
            </div>
          </div>

          {/* New Listing Prices */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              📋 {language === 'sr' ? 'Cene za novi oglas' : 'New listing prices'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
              {language === 'sr' ? 'Važi za Ponude (prodaja/izdavanje) i Potražnje' : 'Applies to Offers (sale/rent) and Demands'}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-600 dark:text-slate-400 font-medium">
                      {language === 'sr' ? 'Trajanje' : 'Duration'}
                    </th>
                    <th className="text-center py-3 px-4 text-slate-600 dark:text-slate-400 font-medium">
                      {language === 'sr' ? 'Cena' : 'Price'}
                    </th>
                    <th className="text-right py-3 px-4 text-slate-600 dark:text-slate-400 font-medium">
                      {language === 'sr' ? 'Po danu' : 'Per day'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {listingPrices.map((item, index) => (
                    <tr 
                      key={index} 
                      className={`border-b border-slate-100 dark:border-slate-700/50 ${item.popular ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}
                    >
                      <td className="py-3 px-4 text-slate-900 dark:text-white">
                        {item.duration}
                        {item.popular && (
                          <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                            {language === 'sr' ? 'Preporučeno' : 'Recommended'}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center font-semibold text-amber-600 dark:text-amber-400">
                        {item.tokens} {item.tokens === 1 ? 'token' : (language === 'sr' ? 'tokena' : 'tokens')}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-500 dark:text-slate-400 text-sm">
                        ~{item.perDay} {language === 'sr' ? 'tokena/dan' : 'tokens/day'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Auction Prices */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              🔨 {language === 'sr' ? 'Cene za aukcije' : 'Auction prices'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
              {language === 'sr' ? 'Dodatna trajanja dostupna za aukcije' : 'Additional durations available for auctions'}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {auctionPrices.map((item, index) => (
                <div key={index} className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3 text-center">
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{item.duration}</p>
                  <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                    {item.tokens} {item.tokens === 1 ? 'token' : 'tokena'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Token Packages */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
              💰 {language === 'sr' ? 'Paketi tokena' : 'Token packages'}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {tokenPackages.map((pkg, index) => {
                const perToken = Math.round(pkg.price / pkg.tokens);
                const isBestValue = perToken === Math.round(bestPerToken);
                return (
                <div 
                  key={index} 
                  className={`relative rounded-xl border-2 p-4 text-center transition-all ${
                    pkg.popular 
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
                      : isBestValue 
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {pkg.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs px-3 py-1 rounded-full">
                      {language === 'sr' ? 'Najpopularniji' : 'Most popular'}
                    </span>
                  )}
                  {isBestValue && !pkg.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                      {language === 'sr' ? 'Najbolja vrednost' : 'Best value'}
                    </span>
                  )}
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">{pkg.tokens}</p>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-2">{language === 'sr' ? 'tokena' : 'tokens'}</p>
                  <p className="text-xl font-semibold text-amber-600 dark:text-amber-400">{pkg.price.toLocaleString()} RSD</p>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{perToken} RSD/{language === 'sr' ? 'token' : 'token'}</p>
                </div>
                );
              })}
            </div>
            <div className="text-center mt-6">
              <Link 
                to="/kupi-tokene" 
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-medium px-6 py-3 rounded-lg transition-colors"
              >
                {language === 'sr' ? 'Kupi tokene' : 'Buy tokens'} →
              </Link>
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
              ❓ {language === 'sr' ? 'Česta pitanja' : 'FAQ'}
            </h2>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">
                  {language === 'sr' ? 'Da li tokeni ističu?' : 'Do tokens expire?'}
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {language === 'sr' 
                    ? 'Ne, tokeni nikada ne ističu. Možete ih koristiti bilo kada nakon kupovine.'
                    : 'No, tokens never expire. You can use them anytime after purchase.'}
                </p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">
                  {language === 'sr' ? 'Šta ako mi oglas istekne?' : 'What if my listing expires?'}
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {language === 'sr' 
                    ? 'Kada oglas istekne, možete ga ponovo aktivirati. Oglas ostaje sačuvan.'
                    : 'When a listing expires, you can reactivate it. The listing remains saved.'}
                </p>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">
                  {language === 'sr' ? 'Kako plaćam tokene?' : 'How do I pay for tokens?'}
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {language === 'sr' 
                    ? 'Tokene možete kupiti uplatom na račun. Instrukcije za uplatu dobijate na stranici za kupovinu tokena.'
                    : 'You can buy tokens via bank transfer. Payment instructions are provided on the buy tokens page.'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
